<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <div class="absolute inset-0 flex flex-col overflow-y-hidden">
      <div class="flex-1 overflow-y-auto">
        <header class="space-y-1 py-6 px-4 bg-cyan-700">
          <div class="flex items-center space-x-3">
            <button
              @click="onGoBack"
              aria-label="Close Panel"
              class="
                text-cyan-200
                hover:text-white
                transition
                ease-in-out
                duration-150
              "
            >
              <svg
                class="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M7.72 12.53a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 1 1 1.06 1.06L9.31 12l6.97 6.97a.75.75 0 1 1-1.06 1.06l-7.5-7.5Z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
            <h2 class="text-lg leading-7 font-medium text-white">ส่งประกาศ</h2>
          </div>
        </header>

        <!-- Error Message -->
        <div
          v-if="errors.length > 0"
          class="px-4 py-3 bg-red-600 bg-opacity-20 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-red-600"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <p class="text-base font-semibold text-red-800 flex-1 px-3">
            {{ errors[0] }}
          </p>
          <button @click="errors = []" type="button" class="focus:outline-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-red-600"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
        <!-- End Error Message -->

        <div class="p-4 space-y-6">
          <div>
            <ValidationProvider
              name="ประเภทการส่งประกาศ"
              rules="required"
              v-slot="{ errors }"
            >
              <AppAutoComplete
                v-model="typeSendAnnouncement"
                :isLoading="loading"
                :isSearch="false"
                :options="[
                  {
                    label: 'ทั้งหมด',
                    value: 'all',
                  },
                  {
                    label: 'บริษัท',
                    value: 'company',
                  },
                ]"
                label="เลือกประเภทการส่งประกาศ"
                item-text-key="label"
                item-value-key="value"
                class="mt-1"
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div
            class="flex flex-col space-y-4"
            v-show="
              typeSendAnnouncement && typeSendAnnouncement.value === 'company'
            "
          >
            <SearchWidgetResultsList v-model="search" />
            <CompaniesCard
              :loading="loading"
              :companies="companies"
              @selectedCompany="onSelectedCompany"
            />
          </div>
        </div>
      </div>

      <div>
        <div class="p-4 border-t flex justify-center">
          <div class="sr-only">{{ isSendAnnouncementDataChange }}</div>
          <div>
            <button
              v-if="!announcement.sent_datetime"
              @click="handleSubmit(onSend)"
              :disabled="loading"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                bg-cyan-600
                text-base
                font-medium
                text-white
                hover:bg-cyan-700
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:w-auto
              "
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16.881 4.345A23.112 23.112 0 0 1 8.25 6H7.5a5.25 5.25 0 0 0-.88 10.427 21.593 21.593 0 0 0 1.378 3.94c.464 1.004 1.674 1.32 2.582.796l.657-.379c.88-.508 1.165-1.593.772-2.468a17.116 17.116 0 0 1-.628-1.607c1.918.258 3.76.75 5.5 1.446A21.727 21.727 0 0 0 18 11.25c0-2.414-.393-4.735-1.119-6.905ZM18.26 3.74a23.22 23.22 0 0 1 1.24 7.51 23.22 23.22 0 0 1-1.41 7.992.75.75 0 1 0 1.409.516 24.555 24.555 0 0 0 1.415-6.43 2.992 2.992 0 0 0 .836-2.078c0-.807-.319-1.54-.836-2.078a24.65 24.65 0 0 0-1.415-6.43.75.75 0 1 0-1.409.516c.059.16.116.321.17.483Z"
                />
              </svg>
              <span class="ml-2">ส่งประกาศ</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import moment from 'moment'

import AppAutoComplete from '@/components/AppAutoComplete'
import CompaniesCard from '@/components/CompaniesCard.vue'
import SearchWidgetResultsList from '@/components/SearchWidgetResultsList'

import { extend } from 'vee-validate'
import { required } from 'vee-validate/dist/rules'

// No message specified.
// extend('required', required)

// Override the default message.
extend('required', {
  ...required,
  message: 'จำเป็นต้องระบุ {_field_}',
})

export default {
  name: 'FormSendAnnouncement',

  props: {
    announcement: {
      type: Object,
    },
  },

  components: {
    AppAutoComplete,
    CompaniesCard,
    SearchWidgetResultsList,
  },

  data() {
    return {
      loading: false,
      search: '',
      errors: [],
      typeSendAnnouncement: null,
      selectedCompanies: [],
    }
  },

  watch: {
    typeSendAnnouncement(value) {
      if (value?.value === 'company') {
        this.fetchCompaniesApi()
      }
    },
  },

  computed: {
    isSendAnnouncementDataChange() {
      if (this.typeSendAnnouncement) {
        this.$emit('isSendAnnouncementDataChange', true)
        return true
      }
      this.$emit('isSendAnnouncementDataChange', false)
      return false
    },

    companies() {
      return this.$store.state.customer.customers.filter((company) => {
        return (
          company.title_th.toLowerCase().includes(this.search.toLowerCase()) ||
          company.company_code.toLowerCase().includes(this.search.toLowerCase())
        )
      })
    },
  },

  methods: {
    onSelectedCompany({ checked, company }) {
      this.selectedCompanies = checked
        ? [...this.selectedCompanies, company]
        : this.selectedCompanies.filter(
            (chooseCompany) =>
              chooseCompany.company_uuid !== company.company_uuid
          )
    },

    onGoBack() {
      this.$emit('onGoBack')
    },

    async onSend() {
      try {
        this.errors = []

        const now = moment().format('DD/MM/YYYY HH:mm')
        const expiredDatetime = this.announcement.expired_datetime
          ? this.announcement.expired_datetime
          : null

        if (expiredDatetime < now) {
          this.errors.push('ไม่สามารถส่งประกาศที่หมดอายุแล้ว')
          return
        }

        if (this.typeSendAnnouncement.value === 'company') {
          if (this.selectedCompanies.length === 0) {
            this.errors.push('กรุณาเลือกบริษัทอย่างน้อย 1 บริษัท')
            return
          }
        }

        const payload = {
          system_announcement_uuid: this.announcement.system_announcement_uuid,
          body: {
            include_all:
              this.typeSendAnnouncement.value === 'all' ? true : false,
            company_uuids:
              this.typeSendAnnouncement.value === 'all'
                ? null
                : this.selectedCompanies.map((company) => company.company_uuid),
          },
        }

        this.loading = true

        await this.$store.dispatch('announcement/sendAnnouncement', payload)
        await this.$store.dispatch('announcement/fetchAnnouncements')

        const notification = {
          type: 'success',
          message: 'Your announcement has been sended!',
        }
        this.$store.dispatch('notification/add', notification)

        this.$emit('isSendAnnouncementDataChange', false)
        this.$emit('onSendAnnouncementSuccess')
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(
            error.response.data.message || 'Something went wrong.'
          )
        }

        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    async fetchCompaniesApi() {
      try {
        this.loading = true

        await this.$store.dispatch('customer/fetchCustomers')
      } catch (error) {
        console.log(error.response)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
